import React, { useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import { getCustomerCompanyManagers, updateCustomerCompanyManager, inviteCustomerTeamMember } from '../../../../services/staffingService';
import { useParams } from 'react-router-dom';
import { useUser } from '../../../../context/UserContext';
import { MoreVertical, Users, X, Loader2, Check } from 'lucide-react';
import CustomerLayout from '../CustomerLayout';
import * as yup from 'yup';
import { useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';

const customerManagerSchema = yup.object({
  customerManagerEmail: yup.string()
    .required('Email is required')
    .email('Must be a valid email address'),
});

const ROLES = [
  { label: 'Admin', value: 'admin' },
];

function getRoleLabel(value) {
  return ROLES.find(r => r.value === value)?.label || value || 'No role assigned';
}

function Avatar({ user }) {
  const initials = user?.firstName
    ? user.firstName.charAt(0).toUpperCase()
    : (user?.email?.charAt(0).toUpperCase() || 'M');

  return user?.profilePictureUrl ? (
    <img
      src={user.profilePictureUrl}
      alt={`${user.firstName} ${user.lastName}`}
      className="h-10 w-10 rounded-full object-cover"
    />
  ) : (
    <div className="flex h-10 w-10 items-center justify-center rounded-full bg-[#404880] text-sm font-semibold text-white">
      {initials}
    </div>
  );
}

const CustomerCompanyTeam = () => {
  const { customerCompanyId } = useParams();
  const { userInfo } = useUser();

  const [loading,          setLoading]          = useState(true);
  const [customerManagers, setCustomerManagers] = useState([]);
  const [open,             setOpen]             = useState(false);
  const [selectedRole,     setSelectedRole]     = useState('admin');
  const [inviteResponse,   setInviteResponse]   = useState('');
  const [inviteError,      setInviteError]      = useState(false);
  const [openMenuIndex,    setOpenMenuIndex]    = useState(null);
  const [menuPos,          setMenuPos]          = useState({ top: 0, right: 0 });
  const [editingIndex,     setEditingIndex]     = useState(null);
  const [savingIndex,      setSavingIndex]      = useState(null);
  const [toast,            setToast]            = useState(null);

  const {
    register: registerManager,
    handleSubmit: handleSubmitManager,
    formState: { errors: errorsManager },
    reset: resetManager,
  } = useForm({ resolver: yupResolver(customerManagerSchema) });

  const fetchManagers = async () => {
    const resp = await getCustomerCompanyManagers(customerCompanyId);
    setCustomerManagers(resp.data || []);
    setLoading(false);
  };

  const onSubmitCustomerManager = async (data) => {
    try {
      await inviteCustomerTeamMember(customerCompanyId, data.customerManagerEmail, selectedRole);
      setInviteResponse('Invitation sent successfully');
      setInviteError(false);
      resetManager();
      fetchManagers();
    } catch (error) {
      const msg = error?.response?.data?.error || 'Failed to send invitation. Please try again.';
      setInviteResponse(msg);
      setInviteError(true);
    }
  };

  const handleRoleChange = (index, value) => {
    setCustomerManagers(prev => prev.map((m, i) => i === index ? { ...m, role: value } : m));
  };

  const handleEditUser   = (index) => { setEditingIndex(index); setOpenMenuIndex(null); };
  const handleSaveEdit   = async () => {
    if (editingIndex === null) return;
    
    setSavingIndex(editingIndex);
    try {
      const manager = customerManagers[editingIndex];
      await updateCustomerCompanyManager(customerCompanyId, manager.user_id._id, { role: manager.role });
      setEditingIndex(null);
    } catch (error) {
      console.error('Error updating manager role:', error);
      alert('Failed to update role. Please try again.');
    } finally {
      setSavingIndex(null);
    }
  };
  const handleCancelEdit = ()      => setEditingIndex(null);

  const showToast = (message) => {
    setToast(message);
    setTimeout(() => setToast(null), 5000);
  };

  const handleRemoveUser = () => {
    setOpenMenuIndex(null);
    showToast('To remove a team member, please contact hello@officer.app');
  };

  useEffect(() => { if (customerCompanyId) fetchManagers(); }, [customerCompanyId]);

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (openMenuIndex !== null && !e.target.closest('.overflow-menu') && !e.target.closest('.menu-dropdown')) {
        setOpenMenuIndex(null);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [openMenuIndex]);

  return (
    <CustomerLayout>
      <main className="flex-1 overflow-y-auto">
        <div className="p-6">
          {/* Page header */}
          <div className="mb-6 flex items-start justify-between">
            <div className="flex items-center gap-3">
              <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-[#404880]/10">
                <Users size={20} className="text-[#404880]" />
              </div>
              <div>
                <h1 className="text-xl font-semibold text-gray-900">Team</h1>
                <p className="text-sm text-gray-500">
                  {customerManagers.length > 0
                    ? `${customerManagers.length} member${customerManagers.length !== 1 ? 's' : ''}`
                    : 'Manage your team members'}
                </p>
              </div>
            </div>
            <button
              onClick={() => { setOpen(true); setInviteResponse(''); }}
              className="flex items-center gap-1.5 h-9 px-4 rounded-lg bg-[#CC457D] text-white text-sm font-medium hover:opacity-90 transition-opacity"
            >
              + Invite
            </button>
          </div>

          {/* Loading */}
          {loading && (
            <div className="flex items-center justify-center py-16">
              <Loader2 size={28} className="animate-spin text-[#404880]" />
            </div>
          )}

          {/* Empty state */}
          {!loading && customerManagers.length === 0 && (
            <div className="flex flex-col items-center justify-center rounded-2xl border border-dashed border-gray-200 bg-white py-20 text-center">
              <div className="mb-3 flex h-14 w-14 items-center justify-center rounded-2xl bg-gray-100">
                <Users size={24} className="text-gray-400" />
              </div>
              <h3 className="text-sm font-semibold text-gray-900">No team members yet</h3>
              <p className="mt-1 text-xs text-gray-500 max-w-xs">
                Invite colleagues to collaborate on shifts and work orders.
              </p>
              <button
                onClick={() => setOpen(true)}
                className="mt-5 rounded-lg bg-[#CC457D] px-5 py-2 text-sm font-medium text-white hover:opacity-90 transition-opacity"
              >
                Send first invite
              </button>
            </div>
          )}

          {/* Team list */}
          {!loading && customerManagers.length > 0 && (
            <div className="rounded-xl border border-gray-200 bg-white divide-y divide-gray-100">
              {customerManagers.map((manager, index) => (
                <div key={manager.user_id._id} className="flex items-center gap-4 px-5 py-4 hover:bg-gray-50 transition-colors">
                  <Avatar user={manager.user_id} />

                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-semibold text-gray-900 truncate">
                      {manager.user_id.firstName && manager.user_id.lastName
                        ? `${manager.user_id.firstName} ${manager.user_id.lastName}`
                        : manager.user_id.email}
                    </p>
                    <p className="text-xs text-gray-400 truncate">{manager.user_id.email}</p>

                    {editingIndex === index ? (
                      <div className="mt-2 flex items-center gap-2 relative z-10">
                        <select
                          value={manager.role || ''}
                          onChange={e => handleRoleChange(index, e.target.value)}
                          className="rounded-lg border border-gray-200 px-2 py-1 text-xs focus:outline-none focus:border-[#404880] bg-white"
                        >
                          <option value="">Select role…</option>
                          {ROLES.map(r => <option key={r.value} value={r.value}>{r.label}</option>)}
                        </select>
                        <button onClick={handleSaveEdit} disabled={savingIndex === index}
                          className="flex items-center gap-1 rounded-lg bg-[#404880] px-2.5 py-1 text-xs font-medium text-white hover:opacity-90 disabled:opacity-50 disabled:cursor-not-allowed">
                          {savingIndex === index ? <Loader2 size={10} className="animate-spin" /> : <Check size={10} />} Save
                        </button>
                        <button onClick={handleCancelEdit}
                          className="rounded-lg border border-gray-200 px-2.5 py-1 text-xs text-gray-500 hover:bg-gray-50">
                          Cancel
                        </button>
                      </div>
                    ) : (
                      <span className="mt-1.5 inline-block rounded-full bg-[#404880]/10 px-2 py-0.5 text-[10px] font-medium text-[#404880]">
                        {getRoleLabel(manager.role)}
                      </span>
                    )}
                  </div>

                  {/* Options menu */}
                  <div className="relative flex-shrink-0">
                    <button
                      className="overflow-menu flex h-8 w-8 items-center justify-center rounded-lg text-gray-400 hover:bg-gray-100 hover:text-gray-600 transition-colors"
                      onClick={e => {
                        e.stopPropagation();
                        if (openMenuIndex === index) {
                          setOpenMenuIndex(null);
                        } else {
                          const rect = e.currentTarget.getBoundingClientRect();
                          setMenuPos({ top: rect.bottom + window.scrollY + 4, right: window.innerWidth - rect.right });
                          setOpenMenuIndex(index);
                        }
                      }}
                    >
                      <MoreVertical size={16} />
                    </button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </main>

      {/* Options dropdown portal */}
      {openMenuIndex !== null && customerManagers[openMenuIndex] && createPortal(
        <div
          className="menu-dropdown fixed w-36 rounded-xl border border-gray-200 bg-white shadow-lg py-1 z-[9997]"
          style={{ top: menuPos.top, right: menuPos.right }}
        >
          <button
            onClick={e => { e.stopPropagation(); handleEditUser(openMenuIndex); }}
            disabled={customerManagers[openMenuIndex].user_id._id === userInfo?._id}
            className="w-full px-3 py-2 text-left text-xs text-gray-700 hover:bg-gray-50 disabled:text-gray-300 disabled:hover:bg-transparent disabled:cursor-not-allowed"
            title={customerManagers[openMenuIndex].user_id._id === userInfo?._id ? "You cannot edit your own role" : "Edit role"}
          >
            Edit role
          </button>
          <button
            onClick={e => { e.stopPropagation(); handleRemoveUser(); }}
            disabled={customerManagers[openMenuIndex].user_id._id === userInfo?._id}
            className="w-full px-3 py-2 text-left text-xs text-red-500 hover:bg-red-50 disabled:text-gray-300 disabled:hover:bg-transparent disabled:cursor-not-allowed"
            title={customerManagers[openMenuIndex].user_id._id === userInfo?._id ? "You cannot remove yourself" : "Remove this member"}
          >
            Remove
          </button>
        </div>,
        document.body
      )}

      {/* Invite modal */}
      {open && createPortal(
        <div className="fixed inset-0 z-[9998] flex items-center justify-center p-4">
          <div className="absolute inset-0 bg-black/40 backdrop-blur-sm" onClick={() => setOpen(false)} />
          <div className="relative z-[9999] w-full max-w-md rounded-2xl bg-white shadow-2xl">
            {/* Header */}
            <div className="flex items-center justify-between border-b border-gray-100 px-6 py-4">
              <div>
                <h2 className="text-base font-semibold text-gray-900">Invite team member</h2>
                <p className="text-xs text-gray-400 mt-0.5">Add a colleague to this account</p>
              </div>
              <button
                onClick={() => setOpen(false)}
                className="flex h-8 w-8 items-center justify-center rounded-lg text-gray-400 hover:bg-gray-100 hover:text-gray-600 transition-colors"
              >
                <X size={16} />
              </button>
            </div>

            {/* Body */}
            <div className="px-6 py-5 space-y-4">
              <form onSubmit={handleSubmitManager(onSubmitCustomerManager)} className="space-y-3">
                <div>
                  <label className="block text-xs text-gray-500 mb-1">Email address</label>
                  <input
                    type="text"
                    {...registerManager('customerManagerEmail')}
                    placeholder="colleague@company.com"
                    className="w-full rounded-lg border border-gray-200 px-3 py-2 text-sm focus:outline-none focus:border-[#404880]"
                  />
                  {errorsManager.customerManagerEmail && (
                    <p className="mt-1 text-xs text-red-500">{errorsManager.customerManagerEmail.message}</p>
                  )}
                </div>

                <div>
                  <label className="block text-xs text-gray-500 mb-1">Role</label>
                  <select
                    value={selectedRole}
                    onChange={e => setSelectedRole(e.target.value)}
                    className="w-full rounded-lg border border-gray-200 px-3 py-2 text-sm text-gray-700 focus:outline-none focus:border-[#404880]"
                  >
                    {ROLES.map(r => <option key={r.value} value={r.value}>{r.label}</option>)}
                  </select>
                </div>

                {inviteResponse && (
                  <p className={`text-xs ${inviteError ? 'text-red-500' : 'text-emerald-600'}`}>{inviteResponse}</p>
                )}

                <button
                  type="submit"
                  className="w-full rounded-lg bg-[#404880] py-2 text-sm font-medium text-white hover:opacity-90 transition-opacity"
                >
                  Send invite
                </button>
              </form>
            </div>
          </div>
        </div>,
        document.body
      )}

      {/* Toast notification */}
      {toast && createPortal(
        <div className="fixed bottom-6 left-1/2 -translate-x-1/2 z-[9999] flex items-center gap-3 rounded-xl bg-gray-900 px-5 py-3 shadow-xl">
          <span className="text-sm text-white">{toast}</span>
          <button onClick={() => setToast(null)} className="text-gray-400 hover:text-white transition-colors">
            <X size={14} />
          </button>
        </div>,
        document.body
      )}
    </CustomerLayout>
  );
};

export default CustomerCompanyTeam;
